import {
  ClipboardList,
  Utensils,
  Pill,
  CalendarClock,
  AlertTriangle,
  Ban,
  CalendarCheck,
  CheckCircle2,
} from "lucide-react";
import { RECOVERY } from "@/data/recovery";
import { SectionHeading, Breadcrumbs, CTABand } from "@/components/Shared";
import { Seo, breadcrumbSchema } from "@/components/Seo";

const ListCard = ({ icon: Icon, title, items, testid, danger }) => (
  <div
    className={`rounded-lg border bg-white p-8 ${danger ? "border-red-200" : "border-brand-line"}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-3">
      <span
        className={`flex h-10 w-10 items-center justify-center rounded-md ${danger ? "bg-red-50 text-red-600" : "bg-brand-soft text-brand-primary"}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <h2 className="font-heading text-xl font-bold text-brand-ink">{title}</h2>
    </div>
    <ul className="mt-5 space-y-3">
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-brand-slate">
          <CheckCircle2
            className={`mt-0.5 h-4 w-4 flex-shrink-0 ${danger ? "text-red-500" : "text-brand-action"}`}
          />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export default function Recovery() {
  return (
    <div data-testid="recovery-page">
      <Seo
        title="Recovery After ENT Surgery — Complete Guide | Dr. Sanjay Teza, Varanasi"
        description="Pre-op instructions, diet, medications, recovery timeline, warning signs, activity restrictions and follow-up care after ENT surgery — guidance from Dr. Sanjay Teza, Varanasi."
        path="/recovery"
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Recovery", path: "/recovery" },
          ]),
        ]}
      />
      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Recovery" }]} />
          <div className="mt-6">
            <SectionHeading
              eyebrow="Recovery After ENT Surgery"
              title="A Smooth, Well-Guided Recovery"
              subtitle="Clear pre-operative and post-operative guidance to help you heal safely and get back to normal life with confidence. Always follow the specific advice given for your surgery."
            />
          </div>
        </div>
      </section>

      <section className="bg-brand-bg">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
          {/* Recovery Timeline */}
          <div className="rounded-lg border border-brand-line bg-white p-8" data-testid="recovery-timeline">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                <CalendarClock className="h-5 w-5" />
              </span>
              <h2 className="font-heading text-xl font-bold text-brand-ink">
                Typical Recovery Timeline
              </h2>
            </div>
            <div className="mt-6 space-y-5">
              {RECOVERY.timeline.map((t) => (
                <div key={t.period} className="flex gap-5 border-l-2 border-brand-soft pl-5">
                  <div>
                    <h3 className="font-heading text-base font-bold text-brand-primary">
                      {t.period}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-brand-slate">
                      {t.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <ListCard
              icon={ClipboardList}
              title="Pre-Operative Instructions"
              items={RECOVERY.preOp}
              testid="recovery-preop"
            />
            <ListCard
              icon={Utensils}
              title="Diet Guidance"
              items={RECOVERY.diet}
              testid="recovery-diet"
            />
            <ListCard
              icon={Pill}
              title="Medications"
              items={RECOVERY.medications}
              testid="recovery-medications"
            />
            <ListCard
              icon={Ban}
              title="Activity Restrictions"
              items={RECOVERY.activityRestrictions}
              testid="recovery-restrictions"
            />
          </div>

          {/* Warning signs */}
          <div className="mt-8">
            <ListCard
              icon={AlertTriangle}
              title="Warning Signs — Contact Us Immediately"
              items={RECOVERY.warningSigns}
              testid="recovery-warning-signs"
              danger
            />
          </div>

          {/* Follow up */}
          <div className="mt-8 rounded-lg border border-brand-line bg-brand-primary p-8" data-testid="recovery-followup">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-white/15 text-white">
                <CalendarCheck className="h-5 w-5" />
              </span>
              <h2 className="font-heading text-xl font-bold text-white">
                Follow-Up Care
              </h2>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-brand-soft">
              {RECOVERY.followUp}
            </p>
          </div>
        </div>
      </section>

      <CTABand
        title="Questions about your recovery?"
        subtitle="Reach Dr. Sanjay Teza's clinic on WhatsApp for post-operative guidance and follow-up scheduling."
        whatsappText="Hello Dr. Sanjay Teza, I have a question about recovery after ENT surgery."
      />
    </div>
  );
}
